import React, { useEffect, useMemo, useState } from 'react';
import axios from 'axios';
import { useNavigate } from 'react-router-dom';
import * as Dialog from '@radix-ui/react-dialog';
import { X } from 'lucide-react';
import CancelButton from '../../../shared/CancelButton';
import SaveButton from '../../../shared/SaveButton';
import RichTextField from '../../../shared/RichTextField';
import RichText from '../../../shared/RichText';
import { renderRealMergePreview } from '../../../shared/MergeFieldRichTextField';
import { mediaFileUrl } from '../../../shared/mediaFileUrl';
import { useStaffAuth } from '../../../shared/StaffAuthContext';
import { formatDate, todayInTenant } from '../../../shared/tenantClock';
import { quoteTotalText } from '../../sales/Quotes/QuoteReportView';
import { paymentDetailLines } from '../../../shared/paymentOptions';
import '../../../shared/StyledInput.css';
import './ComposeEmailModal.css';

const fieldLabelStyle = { fontSize: 12, color: '#5a6b85', textTransform: 'uppercase', letterSpacing: 0.3, display: 'block', marginBottom: 4 };
const fieldWrapStyle = { marginBottom: 14 };

// Compose, preview, and send a templated email to a Commercial Entity
// contact — issue #238, the compose/send story that follows Email
// Template Library (#236) and Attachment Library (#237). One screen, no
// separate confirm step (vis_email_templates.md Concept 5). Laid out as a
// large two-pane modal (form on the left, a big live document preview on
// the right, native browser PDF chrome for zoom/page-nav/print/download) —
// closer to how QuickBooks' own "Send email" screen reads than a narrow
// side drawer, per direct feedback that the original drawer read too
// cramped for actually checking an attachment before sending.
//
// `quoteId` (issue #332) opens it as Email Quote, from a Quote's own page:
// that Quote's PDF is attached, and only templates used for Quotes are
// offered — starting on the one the Customer's Payment Term picks (or the
// built-in Quote – Prepay / Quote template when it picks none). Without it,
// no Quote template is offered at all — a Quote is only ever emailed from
// the Quote.

// '2026-10-17' + 30 days -> '2026-11-16', the same day count the backend
// adds when the Quote moves to Sent.
function addDaysIso(dateOnly, days) {
  const d = new Date(`${dateOnly}T00:00:00Z`);
  d.setUTCDate(d.getUTCDate() + Number(days || 0));
  return d.toISOString().slice(0, 10);
}

function ComposeEmailDrawer({ open, onOpenChange, entity, quoteId, onSaved }) {
  const { currentUser: activeUser } = useStaffAuth();
  const navigate = useNavigate();
  const [recipients, setRecipients] = useState([]);
  // Picked recipients (their place in `recipients`), in pick order — all
  // go in To on one email; the first one supplies the greeting's First
  // Name. Tracked by recipient, not address, since a contact can share the
  // Organisation's address yet still be greeted by their own name.
  const [pickedIndexes, setPickedIndexes] = useState([]);
  const pickedRecipients = pickedIndexes.map((i) => recipients[i]).filter(Boolean);
  const recipientEmails = [...new Set(pickedRecipients.map((r) => r.email))];
  // CC the signed-in user as proof the email went out — on by default,
  // unticked to leave them off this one send.
  const [ccSender, setCcSender] = useState(true);
  // Non-production redirects every send to a fixed test address regardless
  // of who's picked (vis_email_templates.md Concept 11) — surfaced here so
  // staff testing in dev see up front where it's actually going, rather
  // than assuming the picked contact received it.
  const [sendEnvironment, setSendEnvironment] = useState({ nonProduction: false, testEmailAddress: null });
  const [templates, setTemplates] = useState([]);
  const [templateId, setTemplateId] = useState('');
  const [subject, setSubject] = useState('');
  const [bodyHtml, setBodyHtml] = useState('');

  const [pricelists, setPricelists] = useState([]);
  const [pricelistId, setPricelistId] = useState('');
  const [pricelistFormat, setPricelistFormat] = useState('compact');
  const [pricelistPreviewUrl, setPricelistPreviewUrl] = useState('');
  const [pricelistPreviewLoading, setPricelistPreviewLoading] = useState(false);

  // Email Quote mode only: the Quote (same data Print reads) for its merge
  // fields, the tenant's Quotes-valid-for days (a Draft's Valid Until is
  // set by this send), and the attached PDF's preview.
  const [quoteData, setQuoteData] = useState(null);
  const [quoteValidDays, setQuoteValidDays] = useState(null);
  // Company Information, for the Payment Details merge field. Undefined until
  // loaded, so the template isn't filled before it's there.
  const [companySettings, setCompanySettings] = useState(undefined);
  const [quotePreviewUrl, setQuotePreviewUrl] = useState('');
  const [quotePreviewLoading, setQuotePreviewLoading] = useState(false);
  // The subject/body as last filled from the template, so a change of the
  // first recipient can re-fill First Name until staff edit either one.
  const [filled, setFilled] = useState(null);

  const [attachmentFiles, setAttachmentFiles] = useState([]);
  const [selectedMediaIds, setSelectedMediaIds] = useState([]);
  // Blob object URLs for each selected Attachment Library file's preview —
  // fetched via axios rather than pointed straight at mediaFileUrl(id) as
  // an <iframe> src. A plain <img> tag works fine with that URL elsewhere
  // in the app, but an <iframe> navigation sends `Accept: text/html`
  // first, which trips CRA's dev-server proxy into treating it as a page
  // request and serving the SPA's own index.html instead of proxying to
  // the backend. Fetching the bytes ourselves (same technique already
  // used for the Pricelist preview below) sidesteps that entirely.
  const [mediaBlobUrls, setMediaBlobUrls] = useState({});

  // Which attachment tab is showing in the big preview pane — 'email' (the
  // rendered Subject/Body, the fallback when nothing's attached yet) or an
  // attachment's own key. Reset to whichever attachment was just added, so
  // picking one always shows its real content immediately.
  const [activePreviewKey, setActivePreviewKey] = useState('email');

  const [sending, setSending] = useState(false);
  const [error, setError] = useState('');

  const resetForm = () => {
    setPickedIndexes([]);
    setCcSender(true);
    setTemplateId('');
    setSubject('');
    setBodyHtml('');
    setPricelistId('');
    setPricelistFormat('compact');
    setPricelistPreviewUrl('');
    setSelectedMediaIds([]);
    setMediaBlobUrls((prev) => { Object.values(prev).forEach((url) => URL.revokeObjectURL(url)); return {}; });
    setActivePreviewKey('email');
    setFilled(null);
    setQuoteData(null);
    setCompanySettings(undefined);
    setQuotePreviewUrl('');
    setError('');
  };

  useEffect(() => {
    if (!open || !entity?.id) return;
    resetForm();
    axios.get(`/api/commercial-entities/${entity.id}/email-recipients`)
      .then((res) => {
        setRecipients(res.data.data || []);
        setSendEnvironment({ nonProduction: !!res.data.nonProduction, testEmailAddress: res.data.testEmailAddress || null });
      })
      .catch(() => setRecipients([]));
    axios.get('/api/email-templates', { params: { status: 'active' } })
      .then((res) => setTemplates(res.data.data || []))
      .catch(() => setTemplates([]));
    axios.get('/api/media-images', { params: { category: 'attachment' } })
      .then((res) => setAttachmentFiles(res.data.data || []))
      .catch(() => setAttachmentFiles([]));
    // Only Pricelists this Customer is actually in the audience of — e.g.
    // Dealer / Connectors when they're Dealer on Connectors (issue #240).
    if (entity.price_groups?.length) {
      axios.get('/api/pricelists', { params: { status: 'published', for_customer_id: entity.id } })
        .then((res) => setPricelists(res.data.data || []))
        .catch(() => setPricelists([]));
    } else {
      setPricelists([]);
    }
    if (quoteId) {
      axios.get(`/api/quotes/${quoteId}`)
        .then((res) => setQuoteData(res.data.data || null))
        .catch(() => setQuoteData(null));
      axios.get('/api/platform-settings')
        .then((res) => setQuoteValidDays(res.data.data?.quote_valid_days ?? null))
        .catch(() => setQuoteValidDays(null));
      axios.get('/api/company-settings')
        .then((res) => setCompanySettings(res.data.data || null))
        .catch(() => setCompanySettings(null));
      setActivePreviewKey('quote');
      setQuotePreviewLoading(true);
      axios.get(`/api/quotes/${quoteId}/email-pdf`, { responseType: 'blob' })
        .then((res) => setQuotePreviewUrl(URL.createObjectURL(res.data)))
        .catch(() => setQuotePreviewUrl(''))
        .finally(() => setQuotePreviewLoading(false));
    }
    // eslint-disable-next-line
  }, [open, entity?.id, quoteId]);

  useEffect(() => () => { if (quotePreviewUrl) URL.revokeObjectURL(quotePreviewUrl); }, [quotePreviewUrl]);

  // Revokes the previous preview's object URL whenever a new one is made
  // (or the drawer closes) — each is only ever displayed once.
  useEffect(() => () => { if (pricelistPreviewUrl) URL.revokeObjectURL(pricelistPreviewUrl); }, [pricelistPreviewUrl]);

  // The Pricelist attachment travels only with the system Pricelist
  // template (issue #241), found by system_key so a rename can't break it
  // — and that template always needs one, so each such send is tracked.
  const selectedTemplate = templates.find((t) => String(t.id) === String(templateId));
  const isPricelistTemplate = selectedTemplate?.system_key === 'pricelist';
  // Same rule for a Quote template (issue #332): it always carries this
  // Quote, and is only offered when emailing one.
  const isQuoteTemplate = !!selectedTemplate?.for_quotes;
  // The one Email Quote opens on: the Customer's Payment Term's pick, else
  // the built-in Quote – Prepay for a Prepay term, else the built-in Quote.
  // A Prepay Customer with nothing owing always opens on the built-in Quote,
  // so their email doesn't ask them to pay (issue #452).
  const paymentTerm = quoteData?.customer?.payment_term;
  const nothingToPay = paymentTerm?.is_prepay && !quoteData.is_amount_owing;
  const quoteTemplate = (nothingToPay && templates.find((t) => t.system_key === 'quote'))
    || templates.find((t) => t.for_quotes && paymentTerm?.quote_email_template_id && String(t.id) === String(paymentTerm.quote_email_template_id))
    || (paymentTerm?.is_prepay && templates.find((t) => t.system_key === 'quote_prepay'))
    || templates.find((t) => t.system_key === 'quote');
  const templateOptions = templates.filter((t) => !!t.for_quotes === !!quoteId);

  const fillFromTemplate = (template, recipient) => {
    if (!template) { setSubject(''); setBodyHtml(''); setFilled(null); return; }
    const values = {
      // The first picked contact's First Name; an Organisation's own
      // address has no person behind it, so it falls back to the
      // Organisation's name.
      first_name: recipient?.contact_first_name || recipient?.contact_name || entity?.name || '',
      company_name: entity?.name || '',
      sender_name: activeUser?.name || '',
    };
    if (template.for_quotes && quoteData) {
      values.quote_number = quoteData.display_number || '';
      // A Draft's Valid Until starts when this email sends it.
      values.valid_until = quoteData.status === 'draft'
        ? (quoteValidDays !== null ? formatDate(addDaysIso(todayInTenant(), quoteValidDays)) : '')
        : formatDate(quoteData.valid_until) || '';
      values.quote_total = quoteTotalText(quoteData);
      values.po_reference = quoteData.po_reference || '';
      values.bank_details = paymentDetailLines(companySettings, quoteData);
    }
    const { previewSubject, previewBodyHtml } = renderRealMergePreview(template.subject, template.body, values);
    setSubject(previewSubject);
    setBodyHtml(previewBodyHtml);
    setFilled({ subject: previewSubject, bodyHtml: previewBodyHtml });
  };

  const handleTemplateChange = (id) => {
    setTemplateId(id);
    const template = templates.find((t) => String(t.id) === String(id));
    if (template?.system_key !== 'pricelist' && pricelistId) {
      setPricelistId('');
      setPricelistPreviewUrl('');
    }
    fillFromTemplate(template, pickedRecipients[0]);
  };

  // Email Quote opens already filled in from its Quote template, once the
  // template list, the Quote and Company Information have all loaded.
  useEffect(() => {
    if (!open || !quoteId || !quoteTemplate || !quoteData || quoteValidDays === null || companySettings === undefined || templateId) return;
    setTemplateId(String(quoteTemplate.id));
    fillFromTemplate(quoteTemplate, pickedRecipients[0]);
    // eslint-disable-next-line
  }, [open, quoteId, quoteTemplate, quoteData, quoteValidDays, companySettings]);

  // Picking a different first recipient re-fills First Name — unless the
  // subject or body has been edited since the template filled them.
  const firstRecipient = pickedIndexes[0];
  useEffect(() => {
    if (!filled || !selectedTemplate) return;
    if (subject !== filled.subject || bodyHtml !== filled.bodyHtml) return;
    fillFromTemplate(selectedTemplate, pickedRecipients[0]);
    // eslint-disable-next-line
  }, [firstRecipient]);

  const loadPricelistPreview = async (id, format) => {
    if (!id) { setPricelistPreviewUrl(''); return; }
    setPricelistPreviewLoading(true);
    try {
      const res = await axios.post(
        `/api/commercial-entities/${entity.id}/pricelist-pdf-preview`,
        { pricelist_id: id, format },
        { responseType: 'blob' },
      );
      setPricelistPreviewUrl((prev) => { if (prev) URL.revokeObjectURL(prev); return URL.createObjectURL(res.data); });
    } catch {
      setPricelistPreviewUrl('');
    }
    setPricelistPreviewLoading(false);
  };

  const handlePricelistChange = (id) => {
    setPricelistId(id);
    setActivePreviewKey(id ? 'pricelist' : 'email');
    loadPricelistPreview(id, pricelistFormat);
  };

  const handleFormatChange = (format) => {
    setPricelistFormat(format);
    if (pricelistId) loadPricelistPreview(pricelistId, format);
  };

  const toggleMediaId = async (id) => {
    const alreadySelected = selectedMediaIds.includes(id);
    setSelectedMediaIds((prev) => (alreadySelected ? prev.filter((x) => x !== id) : [...prev, id]));

    if (alreadySelected) {
      setMediaBlobUrls((prev) => {
        const { [id]: removed, ...rest } = prev;
        if (removed) URL.revokeObjectURL(removed);
        return rest;
      });
      return;
    }

    setActivePreviewKey(`media-${id}`);
    try {
      const res = await axios.get(mediaFileUrl(id), { responseType: 'blob' });
      setMediaBlobUrls((prev) => ({ ...prev, [id]: URL.createObjectURL(res.data) }));
    } catch {
      // Preview stays unavailable — the checkbox/attachment itself is
      // unaffected, this only governs the big preview pane.
    }
  };

  // The tab strip's contents — the Pricelist (if attached) then each
  // selected Attachment Library file, in pick order. Built once here so
  // both the tab strip and the active iframe's src read off the same list.
  const previewAttachments = useMemo(() => {
    const list = [];
    if (quoteId) {
      list.push({
        key: 'quote',
        label: quoteData?.display_number ? `Quote ${quoteData.display_number}` : 'Quote',
        loading: quotePreviewLoading,
        src: quotePreviewUrl,
      });
    }
    if (pricelistId) {
      const pricelist = pricelists.find((p) => String(p.id) === String(pricelistId));
      list.push({
        key: 'pricelist',
        label: pricelist ? `${pricelist.name} (${pricelistFormat === 'extended' ? 'Extended' : 'Compact'})` : 'Pricelist',
        loading: pricelistPreviewLoading,
        src: pricelistPreviewUrl,
      });
    }
    selectedMediaIds.forEach((id) => {
      const file = attachmentFiles.find((f) => f.id === id);
      list.push({ key: `media-${id}`, label: file?.original_filename || 'Attachment', loading: !mediaBlobUrls[id], src: mediaBlobUrls[id] || '' });
    });
    return list;
  }, [quoteId, quoteData, quotePreviewLoading, quotePreviewUrl, pricelistId, pricelists, pricelistFormat, pricelistPreviewLoading, pricelistPreviewUrl, selectedMediaIds, attachmentFiles, mediaBlobUrls]);

  // If the active tab's attachment gets removed (e.g. its checkbox
  // unticked), fall back to whatever's left, or the email preview.
  useEffect(() => {
    if (activePreviewKey === 'email') return;
    if (!previewAttachments.some((a) => a.key === activePreviewKey)) {
      setActivePreviewKey(previewAttachments[0]?.key || 'email');
    }
    // eslint-disable-next-line
  }, [previewAttachments]);

  const activeAttachment = previewAttachments.find((a) => a.key === activePreviewKey);

  const handleClose = (nextOpen) => {
    if (!nextOpen) resetForm();
    onOpenChange(nextOpen);
  };

  const canSend = !!(recipientEmails.length && subject && bodyHtml && !(isPricelistTemplate && !pricelistId) && !(quoteId && !isQuoteTemplate));
  const handleSend = async () => {
    if (!canSend) return;
    setSending(true);
    setError('');
    try {
      const res = await axios.post(`/api/commercial-entities/${entity.id}/send-templated-email`, {
        email_template_id: templateId || null,
        recipient_emails: recipientEmails,
        cc_sender: ccSender,
        subject,
        body_html: bodyHtml,
        pricelist_attachment: isPricelistTemplate && pricelistId ? { pricelist_id: pricelistId, format: pricelistFormat } : null,
        quote_attachment: isQuoteTemplate && quoteId ? { quote_id: quoteId } : null,
        media_image_ids: selectedMediaIds,
      });
      onSaved(res.data.data);
      handleClose(false);
    } catch (err) {
      setError(err.response?.data?.message || 'Send failed');
    }
    setSending(false);
  };

  return (
    <Dialog.Root open={open} onOpenChange={handleClose}>
      <Dialog.Portal>
        <Dialog.Overlay className="compose-email-overlay" />
        <Dialog.Content className="compose-email-content">
          <div className="compose-email-header">
            <Dialog.Title className="compose-email-title">{quoteId ? 'Email Quote' : 'Compose Email'}</Dialog.Title>
            <Dialog.Close asChild>
              <button type="button" className="compose-email-close" aria-label="Close">
                <X size={16} strokeWidth={2} />
              </button>
            </Dialog.Close>
          </div>

          <div className="compose-email-body">
            <div className="compose-email-form-pane">
              {sendEnvironment.nonProduction && (
                sendEnvironment.testEmailAddress ? (
                  <div style={{ fontSize: 12, color: '#8a5a00', background: '#fff8e6', border: '1px solid #f0d9a6', borderRadius: 4, padding: '8px 10px', marginBottom: 14 }}>
                    Non-production: This will be delivered to <strong>{sendEnvironment.testEmailAddress}</strong> regardless of the selected recipients.
                  </div>
                ) : (
                  <div style={{ fontSize: 12, color: '#b91c1c', background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 4, padding: '8px 10px', marginBottom: 14 }}>
                    Non-production: sending is blocked until a Test Email Address is set on{' '}
                    <button type="button" className="link-button" onClick={() => { handleClose(false); navigate('/global-platform-settings'); }}>
                      Platform Settings
                    </button>.
                  </div>
                )
              )}

              <div style={fieldWrapStyle}>
                <label style={fieldLabelStyle}>To</label>
                <select
                  className="styled-input"
                  value=""
                  onChange={(e) => { const i = e.target.value; if (i !== '') setPickedIndexes((prev) => [...prev, Number(i)]); }}
                  style={{ width: '100%', fontSize: 14 }}
                  disabled={recipients.length > 0 && pickedIndexes.length >= recipients.length}
                >
                  <option value="">{pickedIndexes.length ? '(add another recipient)' : '(select a recipient)'}</option>
                  {recipients.map((r, i) => (pickedIndexes.includes(i) ? null : (
                    <option key={i} value={i}>{r.label}</option>
                  )))}
                </select>
                {pickedIndexes.length > 0 && (
                  <div className="compose-email-recipient-chips">
                    {pickedIndexes.map((i) => (
                      <span key={i} className="compose-email-recipient-chip">
                        {recipients[i]?.label || recipients[i]?.email}
                        <button
                          type="button"
                          aria-label={`Remove ${recipients[i]?.label || recipients[i]?.email}`}
                          title="Remove recipient"
                          onClick={() => setPickedIndexes((prev) => prev.filter((x) => x !== i))}
                        >
                          <X size={12} strokeWidth={2.5} />
                        </button>
                      </span>
                    ))}
                  </div>
                )}
                {recipients.length === 0 && (
                  <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>No stored email addresses found on this record or its contacts.</div>
                )}
              </div>

              {activeUser?.email && (
                <div style={fieldWrapStyle}>
                  <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer' }}>
                    <input type="checkbox" checked={ccSender} onChange={(e) => setCcSender(e.target.checked)} />
                    CC me ({activeUser.email})
                  </label>
                  {sendEnvironment.nonProduction && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>Non-production: the CC is not sent.</div>
                  )}
                </div>
              )}

              <div style={fieldWrapStyle}>
                <label style={fieldLabelStyle}>Template</label>
                <select className="styled-input" value={templateId} onChange={(e) => handleTemplateChange(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
                  {!quoteId && <option value="">(select a template)</option>}
                  {quoteId && !templateId && <option value="">Loading...</option>}
                  {templateOptions.map((t) => (
                    <option key={t.id} value={t.id}>{t.name}</option>
                  ))}
                </select>
              </div>

              <div style={fieldWrapStyle}>
                <label style={fieldLabelStyle}>Subject</label>
                <input
                  className="styled-input"
                  type="text"
                  value={subject}
                  onChange={(e) => setSubject(e.target.value)}
                  style={{ width: '100%', fontSize: 14 }}
                />
              </div>

              <div style={fieldWrapStyle}>
                <label style={fieldLabelStyle}>Body</label>
                <RichTextField value={bodyHtml} onChange={setBodyHtml} placeholder="Email body..." />
              </div>

              {isPricelistTemplate && (
                <div style={fieldWrapStyle}>
                  <label style={fieldLabelStyle}>Attach a Pricelist</label>
                  <select className="styled-input" value={pricelistId} onChange={(e) => handlePricelistChange(e.target.value)} style={{ width: '100%', fontSize: 14 }}>
                    <option value="">(select a pricelist)</option>
                    {pricelists.map((p) => (
                      <option key={p.id} value={p.id}>{p.name}</option>
                    ))}
                  </select>
                  {pricelistId && (
                    <div style={{ marginTop: 8, display: 'flex', gap: 16 }}>
                      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="radio" checked={pricelistFormat === 'compact'} onChange={() => handleFormatChange('compact')} /> Compact
                      </label>
                      <label style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 4 }}>
                        <input type="radio" checked={pricelistFormat === 'extended'} onChange={() => handleFormatChange('extended')} /> Extended
                      </label>
                    </div>
                  )}
                  {pricelists.length === 0 && (
                    <div style={{ fontSize: 12, color: '#888', marginTop: 4 }}>No published pricelists match this record's Price Group.</div>
                  )}
                </div>
              )}

              <div style={fieldWrapStyle}>
                <label style={fieldLabelStyle}>Attach Files from the Attachment Library (optional)</label>
                {attachmentFiles.length === 0 && <div style={{ fontSize: 12, color: '#888' }}>No files in the Attachment Library yet.</div>}
                {attachmentFiles.map((file) => (
                  <label key={file.id} style={{ fontSize: 13, display: 'flex', alignItems: 'center', gap: 6, cursor: 'pointer', marginBottom: 6 }}>
                    <input type="checkbox" checked={selectedMediaIds.includes(file.id)} onChange={() => toggleMediaId(file.id)} />
                    {file.original_filename}
                  </label>
                ))}
              </div>

              {error && <div style={{ color: 'red', fontSize: 13, marginTop: 8 }}>{error}</div>}
            </div>

            <div className="compose-email-preview-pane">
              <div className="compose-email-preview-tabs">
                <button
                  type="button"
                  className={`compose-email-preview-tab${activePreviewKey === 'email' ? ' active' : ''}`}
                  onClick={() => setActivePreviewKey('email')}
                >
                  Email
                </button>
                {previewAttachments.map((a) => (
                  <button
                    key={a.key}
                    type="button"
                    className={`compose-email-preview-tab${activePreviewKey === a.key ? ' active' : ''}`}
                    onClick={() => setActivePreviewKey(a.key)}
                    title={a.label}
                  >
                    {a.label}
                  </button>
                ))}
              </div>

              {activePreviewKey === 'email' ? (
                <div style={{ flex: 1, overflowY: 'auto', padding: 24 }}>
                  <div style={{ maxWidth: 700, margin: '0 auto', background: '#fff', border: '1px solid #d7dbe3', borderRadius: 6, padding: 24 }}>
                    <div style={{ fontWeight: 600, fontSize: 16, marginBottom: 14, paddingBottom: 14, borderBottom: '1px solid #eef0f3' }}>
                      {subject || <span style={{ color: '#aaa', fontWeight: 400 }}>(no subject yet)</span>}
                    </div>
                    {bodyHtml ? <RichText html={bodyHtml} style={{ fontSize: 14 }} /> : <div style={{ color: '#aaa', fontSize: 14 }}>Pick a template, or start typing a body, to see it here.</div>}
                  </div>
                </div>
              ) : activeAttachment?.loading ? (
                <div className="compose-email-preview-empty" style={{ color: '#888', fontSize: 14 }}>Generating preview...</div>
              ) : activeAttachment?.src ? (
                <iframe title={activeAttachment.label} src={activeAttachment.src} className="compose-email-preview-frame" />
              ) : (
                <div className="compose-email-preview-empty" style={{ color: '#888', fontSize: 14 }}>No preview available.</div>
              )}
            </div>
          </div>

          <div className="compose-email-footer">
            <div style={{ display: 'flex', gap: 8, marginLeft: 'auto' }}>
              <CancelButton onClick={() => handleClose(false)} disabled={sending} />
              <SaveButton onClick={handleSend} loading={sending} disabled={!canSend} type="button">
                {sending ? 'Sending...' : 'Send'}
              </SaveButton>
            </div>
          </div>
        </Dialog.Content>
      </Dialog.Portal>
    </Dialog.Root>
  );
}

export default ComposeEmailDrawer;
